import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch, useForm } from "react-hook-form"
import { EventFormData } from "@/types/events"
import { format } from "date-fns"
import { Calendar as CalendarIcon, Clock } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { useState, useMemo, useEffect } from "react"
import { Switch } from "@/components/ui/switch"
import { ScrollArea } from "@/components/ui/scroll-area"
import { TimezoneSelect } from "./datetime/TimezoneSelect"

interface EventDateTimeProps {
  register: UseFormRegister<EventFormData>
  errors: FieldErrors<EventFormData>
  setValue: UseFormSetValue<EventFormData>
  watch: UseFormWatch<EventFormData>
  onDateChange?: (date: string) => void
  onTimezoneChange?: (timezone: string) => void
}

const generateTimeSlots = (is24Hour: boolean) => {
  return Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2)
    const minute = i % 2 === 0 ? "00" : "30"
    
    if (is24Hour) {
      return {
        label: `${hour.toString().padStart(2, '0')}:${minute}`,
        value: `${hour.toString().padStart(2, '0')}:${minute}`
      }
    }

    const ampm = hour >= 12 ? "PM" : "AM"
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return {
      label: `${displayHour}:${minute} ${ampm}`,
      value: `${displayHour}:${minute} ${ampm}`
    }
  })
}

const convertTime = (time: string | undefined, to24Hour: boolean): string => {
  if (!time) return ""
  
  // If already in the target format, return as is
  const is24HourFormat = !time.includes("AM") && !time.includes("PM")
  if (is24HourFormat === to24Hour) return time
  
  const [timeStr, meridiem] = time.split(" ")
  const [hours, minutes] = timeStr.split(":")
  let hour = parseInt(hours)
  
  if (to24Hour) {
    // Convert 12h to 24h
    if (meridiem === "PM" && hour !== 12) hour += 12
    if (meridiem === "AM" && hour === 12) hour = 0
    return `${hour.toString().padStart(2, '0')}:${minutes}`
  } else {
    // Convert 24h to 12h
    const ampm = hour >= 12 ? "PM" : "AM"
    hour = hour % 12 || 12
    return `${hour}:${minutes} ${ampm}`
  }
}

export const EventDateTime = ({ register, errors, setValue, watch, onDateChange, onTimezoneChange }: EventDateTimeProps) => {
  const [is24Hour, setIs24Hour] = useState(false);
  const eventDate = watch("event_date");
  
  // Initialize date state with form value
  const [date, setDate] = useState<Date | undefined>(() => {
    return eventDate ? new Date(eventDate) : undefined;
  });

  // Calculate minimum date (2 weeks from today)
  const minDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 14); // Add 14 days
    date.setHours(0, 0, 0, 0); // Set to start of day
    return date;
  }, []);

  // Keep date state in sync with form value
  useEffect(() => {
    if (eventDate && (!date || format(new Date(eventDate), "yyyy-MM-dd") !== format(date, "yyyy-MM-dd"))) {
      setDate(new Date(eventDate));
    }
  }, [eventDate, date]);

  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (e) {
      return "UTC"
    }
  });
  
  // Update the form value when timezone changes
  useEffect(() => {
    setValue("timezone", selectedTimezone);
    if (onTimezoneChange) {
      onTimezoneChange(selectedTimezone);
    }
  }, [selectedTimezone, setValue, onTimezoneChange]);

  const twoDaysFromNow = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() + 2)
    return date
  }, [])

  const timeSlots = useMemo(() => generateTimeSlots(is24Hour), [is24Hour])
  const currentTime = watch("event_time")

  const handleTimeFormatChange = (use24Hour: boolean) => {
    setIs24Hour(use24Hour)
    const newTime = convertTime(currentTime, use24Hour)
    if (newTime) {
      setValue("event_time", newTime)
    }
  }

  const handleDateSelect = (newDate: Date | undefined) => {
    setDate(newDate)
    if (newDate) {
      const formattedDate = format(newDate, "yyyy-MM-dd")
      setValue("event_date", formattedDate)
      onDateChange?.(formattedDate)
    } else {
      setValue("event_date", "")
      onDateChange?.("")
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label>Event Date{" "}<span className="text-red-500">*</span></Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                fromDate={minDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {errors.event_date && (
            <p className="text-sm text-red-500 mt-1">{errors.event_date.message}</p>
          )}
        </div>

        <div>
          <Label>Event Time{" "}<span className="text-red-500">*</span></Label>
          <Select
            value={currentTime || ""}
            onValueChange={(value) => setValue("event_time", value)}
          >
            <SelectTrigger className="w-full">
              <div className="flex items-center">
                <Clock className="mr-2 h-4 w-4" />
                {currentTime || "Select time"}
              </div>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[200px]">
                {timeSlots.map((time) => (
                  <SelectItem key={time.value} value={time.value}>
                    {time.label}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
          {errors.event_time && (
            <p className="text-sm text-red-500 mt-1">{errors.event_time.message}</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Time Format</Label>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">12h</span>
              <Switch
                checked={is24Hour}
                onCheckedChange={handleTimeFormatChange}
              />
              <span className="text-sm text-muted-foreground">24h</span>
            </div>
          </div>

          <TimezoneSelect 
            value={selectedTimezone} 
            onChange={(timezone) => {
              setSelectedTimezone(timezone);
              setValue("timezone", timezone);
              if (onTimezoneChange) {
                onTimezoneChange(timezone);
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}